import { dismissFormFocus } from '../lib/form-focus.js'
import {
  BRAND,
  BrandMark,
  Button,
  Callout,
  createI18n,
  Icon,
  Sheet,
  Skeleton,
  type I18n,
} from '@signbento/ui'
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom'
import { useRouteScroll } from '../lib/route-scroll.js'
import { WorkspaceFrameContext } from './workspace-frame.js'
import {
  NewDeviceHint,
  NotFound,
  OfflineNotice,
  RouteErrorBoundary,
  StorageUnavailable,
} from './ShellStates.js'
import { isOutsideShell } from '../lib/shell-state.js'
import { purgeExpiredTemplates } from '../lib/template-maintenance.js'
import { productTelemetry } from '../lib/product-telemetry.js'
import { useVault } from '../lib/vault-context.js'
import { onAccountIdentity, readAccountIdentity } from '../lib/account-identity.js'
import { signInRequired } from '../lib/device-accounts.js'
import { startNativeSignInReturns } from '../lib/native-sign-in-returns.js'
import { DeletionReceiptView } from '../routes/DeletionReceiptView.js'
import { ensurePdfWorker } from '../lib/pdf-worker.js'
import {
  mobilePrimaryDestinations,
  MORE_SHORTCUTS,
  moreDestinations,
  NAV_DESTINATIONS,
  navSectionFor,
  type NavDestination,
} from './navigation.js'

const Home = lazy(() => import('../routes/Home.js').then((module) => ({ default: module.Home })))
const Agreements = lazy(() =>
  import('../routes/AgreementsPage.js').then((module) => ({ default: module.AgreementsPage })),
)
const RecentlyDeletedDocuments = lazy(() =>
  import('../routes/DocumentTrash.js').then((module) => ({
    default: module.RecentlyDeletedDocuments,
  })),
)
const Insights = lazy(() =>
  import('../routes/Insights.js').then((module) => ({ default: module.Insights })),
)
const NativeChallenge = lazy(() =>
  import('../routes/NativeChallenge.js').then((module) => ({ default: module.NativeChallenge })),
)
const SignIn = lazy(() =>
  import('../routes/SignIn.js').then((module) => ({ default: module.SignIn })),
)
const PurchasesReturn = lazy(() =>
  import('../routes/PurchasesReturn.js').then((module) => ({ default: module.PurchasesReturn })),
)
// The handoff page previews the uploaded PDF and the review page renders the frozen copy, so both
// load the PDF engine's worker like every other document route.
const McpHandoff = lazy(async () => {
  const [module] = await Promise.all([import('../routes/McpHandoff.js'), ensurePdfWorker()])
  return { default: module.McpHandoff }
})
const McpConnect = lazy(() =>
  import('../routes/McpConnect.js').then((module) => ({ default: module.McpConnect })),
)
const McpReviews = lazy(async () => {
  const [module] = await Promise.all([import('../routes/McpReviews.js'), ensurePdfWorker()])
  return { default: module.McpReviews }
})
const OpsCosts = lazy(() =>
  import('../routes/OpsCosts.js').then((module) => ({ default: module.OpsCosts })),
)
const DocumentEditor = lazy(async () => {
  const [module] = await Promise.all([import('../routes/DocumentEditor.js'), ensurePdfWorker()])
  return { default: module.DocumentEditor }
})
const ImportPanel = lazy(async () => {
  const [module] = await Promise.all([import('../routes/ImportPanel.js'), ensurePdfWorker()])
  return { default: module.ImportPanel }
})
const Templates = lazy(async () => {
  const [module] = await Promise.all([import('../routes/Templates.js'), ensurePdfWorker()])
  return { default: module.Templates }
})
const Settings = lazy(async () => {
  const [module] = await Promise.all([import('../routes/Settings.js'), ensurePdfWorker()])
  return { default: module.Settings }
})
const SendPanel = lazy(async () => {
  const [module] = await Promise.all([import('../routes/SendPanel.js'), ensurePdfWorker()])
  return { default: module.SendPanel }
})
const OpsHealth = lazy(() =>
  import('../routes/OpsHealth.js').then((module) => ({ default: module.OpsHealth })),
)
const AccountAction = lazy(() =>
  import('../routes/AccountAction.js').then((module) => ({ default: module.AccountAction })),
)
const RequestDetail = lazy(() =>
  import('../routes/RequestDetail.js').then((module) => ({ default: module.RequestDetail })),
)
const RecipientSign = lazy(async () => {
  const [module] = await Promise.all([import('../routes/RecipientSign.js'), ensurePdfWorker()])
  return { default: module.RecipientSign }
})
const CcFinal = lazy(() =>
  import('../routes/CcFinal.js').then((module) => ({ default: module.CcFinal })),
)

export interface AppProps {
  readonly i18n?: I18n
}

/**
 * Vault maintenance on app open (S2.2): the retention timer removes templates
 * whose 30-day window has passed. Best effort — a maintenance failure never
 * blocks the app, and the timer is idempotent, so a later open retries it.
 */
function useTemplateMaintenance(): void {
  const { vault, ready } = useVault()
  useEffect(() => {
    if (!ready || vault === null) return
    void purgeExpiredTemplates({ vault, now: new Date() }).catch(() => undefined)
  }, [vault, ready])
}

/**
 * A template opened from its Edit works on a scratch copy. Left some other way than the editor's
 * own Back (the browser's Back, the sidebar, a closed tab), that copy is cleared away once the
 * person is elsewhere, so it never lingers as a draft in Agreements.
 */
function useTemplateScratchSweep(pathname: string): void {
  const { vault, ready } = useVault()
  useEffect(() => {
    if (!ready || vault === null) return
    const open = /^\/agreements\/([^/]+)\/?$/.exec(pathname)?.[1] ?? null
    const timer = window.setTimeout(() => {
      void import('../lib/template-scratch.js')
        .then(async ({ sweepTemplateScratch, TEMPLATE_SCRATCH_SWEPT_EVENT }) => {
          const removed = await sweepTemplateScratch({
            vault,
            open_document_id: open,
            now: new Date(),
          })
          if (removed > 0) window.dispatchEvent(new Event(TEMPLATE_SCRATCH_SWEPT_EVENT))
        })
        .catch(() => undefined)
    }, 0)
    return () => window.clearTimeout(timer)
  }, [vault, ready, pathname])
}

/**
 * Product telemetry starts honouring the saved choice once the vault is open (§16.3, BRD-04).
 * Until then nothing is tracked. A vault that failed to open falls back to the default.
 */
function useTelemetryPreference(): void {
  const { vault, ready } = useVault()
  useEffect(() => {
    if (!ready) return
    void productTelemetry.load(vault).catch(() => undefined)
  }, [vault, ready])
}

/**
 * The email this device is signed in with, for the shell to say who is signed in (the sidebar's
 * foot and the phone's More sheet). Null for a guest or before the vault has opened. Read again
 * when sign-in announces it, since sign-in writes the email after the account's vault opens.
 */
function useSignedInEmail(): string | null {
  const { vault, ready, account } = useVault()
  const [email, setEmail] = useState<string | null>(null)
  const [written, setWritten] = useState(0)
  useEffect(() => onAccountIdentity(() => setWritten((count) => count + 1)), [])
  useEffect(() => {
    if (!ready || vault === null) {
      setEmail(null)
      return
    }
    let cancelled = false
    void readAccountIdentity(vault)
      .then((identity) => {
        if (!cancelled) setEmail(identity.account_id === null ? null : identity.email)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [vault, ready, account, written])
  return email
}

/** Shown while a route's code loads (UX-34): the shape of a page header and its first card. */
function RouteSkeleton({ i18n }: { readonly i18n: I18n }) {
  return (
    <div className="app-page app-skeleton" role="status">
      <span className="op-visually-hidden">{i18n.t('common.loading')}</span>
      <Skeleton size="title" width="md" />
      <Skeleton size="text" width="lg" />
      <Skeleton size="block" width="full" lines={4} />
    </div>
  )
}

/** The brand above one full-page state: the sign-in gate, or a browser that won't keep data. */
function GateFrame({
  testId,
  children,
}: {
  readonly testId: string
  readonly children: ReactNode
}): ReactElement {
  return (
    <main className="app-gate" data-testid={testId}>
      <div className="app-gate__brand">
        <BrandMark size={36} />
        <Wordmark />
      </div>
      {children}
    </main>
  )
}

/** The whole app before an account is bound on this device: the brand and sign-in, nothing else. */
function SignInGate({ i18n }: { readonly i18n: I18n }) {
  const { notice } = useVault()
  return (
    <GateFrame testId="sign-in-gate">
      {notice?.kind === 'deleted' ? (
        <DeletionReceiptView i18n={i18n} receipt={notice.receipt} />
      ) : notice?.kind === 'switched' ? (
        <Callout tone="info" role="status">
          <p data-testid="sign-in-gate-switched">{i18n.t('signin.gate_switched')}</p>
        </Callout>
      ) : notice?.kind === 'signed_out' ? (
        <Callout tone="info" role="status">
          <p data-testid="sign-in-gate-signed-out">{i18n.t('signin.gate_signed_out')}</p>
        </Callout>
      ) : null}
      <Suspense fallback={<RouteSkeleton i18n={i18n} />}>
        <SignIn i18n={i18n} />
      </Suspense>
    </GateFrame>
  )
}

/** The page each history entry was opened from, by the router's entry key. */
const openedFrom = new Map<string, string>()

/**
 * What the phone's back control says: where it goes. Back through the app's history it names the
 * section it returns to (Home, Agreements) when that page is one; a page opened straight from a
 * link names the section it goes up to. Only a return to a deeper page says plain "Back", so
 * Sign and Send say the same thing however they were reached.
 */
function useBackLabel(i18n: I18n, canGoBack: boolean, parentLabel: string): string {
  const location = useLocation()
  const navigationType = useNavigationType()
  const last = useRef<{ key: string; path: string } | null>(null)
  const previous =
    openedFrom.get(location.key) ??
    (navigationType === 'PUSH' && last.current?.key !== location.key
      ? last.current?.path
      : undefined)
  useEffect(() => {
    const before = last.current
    if (before !== null && before.key !== location.key && !openedFrom.has(location.key)) {
      if (navigationType === 'PUSH') openedFrom.set(location.key, before.path)
      else if (navigationType === 'REPLACE') {
        const inherited = openedFrom.get(before.key)
        if (inherited !== undefined) openedFrom.set(location.key, inherited)
      }
    }
    last.current = { key: location.key, path: location.pathname }
  }, [location.key, location.pathname, navigationType])
  if (!canGoBack) return parentLabel
  const section =
    previous === undefined
      ? undefined
      : NAV_DESTINATIONS.find(
          (destination) => destination.path === (previous.replace(/\/+$/, '') || '/'),
        )
  return section === undefined ? i18n.t('common.back') : i18n.t(section.label_key)
}

function Shell({ i18n }: { readonly i18n: I18n }) {
  const [moreOpen, setMoreOpen] = useState(false)
  const location = useLocation()
  useEffect(() => {
    dismissFormFocus(document)
  }, [location.key])
  useEffect(() => {
    const dismiss = (): void => dismissFormFocus(document)
    window.addEventListener('signbento:modal-open', dismiss)
    return () => window.removeEventListener('signbento:modal-open', dismiss)
  }, [])

  const more = moreDestinations()
  useTemplateMaintenance()
  useTemplateScratchSweep(location.pathname)
  useRouteScroll()
  useTelemetryPreference()
  const navigate = useNavigate()
  // The installed app: a sign-in return (Apple/Google, the email's link) opens the sign-in screen
  // wherever the app was, or was relaunched (DEV-022/023).
  useEffect(() => {
    void startNativeSignInReturns(() => void navigate('/sign-in'))
  }, [navigate])
  const { ready: vaultReady, account, vault, error: vaultError } = useVault()
  const signedInEmail = useSignedInEmail()
  // The document editor is a full-height workspace (UX-26): no page padding, and on phones no
  // bottom nav under its own palette bar.
  const [documentPage, setDocumentPage] = useState(false)
  const workspace =
    /^\/agreements\/[^/]+\/?$/.test(location.pathname) &&
    !/^\/agreements\/deleted\/?$/.test(location.pathname) &&
    !documentPage
  // Every route highlights one navigation entry (UX-34); a screen below the top level also gets
  // a back control in the phone top bar.
  const section = navSectionFor(location.pathname)
  const parentLabel = i18n.t(
    NAV_DESTINATIONS.find((destination) => destination.path === section.parent)?.label_key ??
      'nav.home',
  )
  // The phone's back control goes back through the app's own history when there is an earlier
  // page in it (a request opened from Home returns to Home); a page opened straight from a link
  // goes up to its section instead. The router keys the first page of a visit "default".
  const canGoBack = location.key !== 'default'
  const backLabel = useBackLabel(i18n, canGoBack, parentLabel)
  const navLink = (
    destination: NavDestination,
    className = 'app-nav-link',
    onClick?: () => void,
  ) => {
    const active = destination.id === section.id
    return (
      <Link
        to={destination.path}
        className={active ? `${className} is-active` : className}
        aria-current={active ? (section.parent === null ? 'page' : 'true') : undefined}
        onClick={onClick}
      >
        <Icon name={destination.icon} />
        <span className="app-nav-label">{i18n.t(destination.label_key)}</span>
      </Link>
    )
  }

  // A browser that won't keep data on the device (a private window, blocked site data, a full
  // disk): nothing local can work, so this says why instead of pages that load forever.
  if (vaultReady && vault === null && vaultError !== null) {
    return (
      <GateFrame testId="storage-gate">
        <StorageUnavailable i18n={i18n} reason={vaultError} />
      </GateFrame>
    )
  }

  // Sign in once per device (sign-in-once spec §1): behind the gate, nothing but sign-in renders
  // until an account is bound, whatever page was opened; that page renders once signed in.
  if (signInRequired()) {
    if (!vaultReady) return <RouteSkeleton i18n={i18n} />
    if (account === null || account === undefined) return <SignInGate i18n={i18n} />
  }

  return (
    <WorkspaceFrameContext.Provider value={setDocumentPage}>
      <div className={workspace ? 'app app--workspace' : 'app'}>
        {/* The first stop for the keyboard, so every page doesn't cost a pass through the nav. */}
        <a
          className="app-skip-link"
          href="#app-main"
          data-testid="skip-to-content"
          onClick={(event) => {
            event.preventDefault()
            document.getElementById('app-main')?.focus()
          }}
        >
          {i18n.t('nav.skip_to_content')}
        </a>
        <aside className="app-sidebar">
          <div className="app-brand">
            <BrandMark size={36} />
            <Wordmark className="app-brand__name" />
          </div>
          <nav className="app-nav" aria-label={i18n.t('nav.main_label')}>
            <ul>
              {NAV_DESTINATIONS.map((destination) => (
                <li key={destination.id}>{navLink(destination)}</li>
              ))}
            </ul>
          </nav>
          {signedInEmail === null ? null : (
            <Link
              to="/settings?section=account"
              className="app-sidebar__account"
              data-testid="sidebar-account"
              title={signedInEmail}
            >
              <Icon name="user" />
              <span className="app-sidebar__account-text">
                <span className="app-sidebar__account-label">{i18n.t('nav.signed_in_as')}</span>
                <span className="app-sidebar__account-email">{signedInEmail}</span>
              </span>
            </Link>
          )}
        </aside>

        {workspace ? null : (
          <header className="app-topbar" data-testid="app-topbar">
            {section.parent === null ? (
              <span className="app-topbar__brand">
                <BrandMark size={24} />
                <Wordmark />
              </span>
            ) : (
              <Link
                className="app-topbar__back"
                to={section.parent}
                data-testid="app-topbar-back"
                onClick={(event) => {
                  if (!canGoBack) return
                  event.preventDefault()
                  void navigate(-1)
                }}
              >
                <Icon name="chevron-left" />
                <span>{backLabel}</span>
              </Link>
            )}
          </header>
        )}

        <main
          id="app-main"
          tabIndex={-1}
          className={workspace ? 'app-main app-main--workspace' : 'app-main'}
          key={location.pathname}
        >
          {workspace ? null : <OfflineNotice i18n={i18n} />}
          {location.pathname === '/' ? <NewDeviceHint i18n={i18n} vault={vault} /> : null}
          <RouteErrorBoundary i18n={i18n} resetKey={location.pathname}>
            <Suspense fallback={<RouteSkeleton i18n={i18n} />}>
              <Routes>
                <Route path="/" element={<Home i18n={i18n} />} />
                <Route path="/agreements" element={<Agreements i18n={i18n} />} />
                <Route
                  path="/agreements/deleted"
                  element={<RecentlyDeletedDocuments i18n={i18n} />}
                />
                <Route path="/agreements/:id" element={<DocumentEditor i18n={i18n} />} />
                <Route path="/templates" element={<Templates i18n={i18n} />} />
                <Route path="/insights" element={<Insights i18n={i18n} />} />
                <Route path="/settings" element={<Settings i18n={i18n} />} />
                <Route path="/sign" element={<ImportPanel i18n={i18n} />} />
                <Route path="/send" element={<SendPanel i18n={i18n} />} />
                <Route
                  path="/requests"
                  element={<Navigate to="/agreements?view=attention" replace />}
                />
                <Route path="/requests/:id" element={<RequestDetail i18n={i18n} />} />
                <Route path="/sign-in" element={<SignIn i18n={i18n} />} />
                <Route path="/account-actions/:id" element={<AccountAction i18n={i18n} />} />
                <Route path="/purchases/return" element={<PurchasesReturn i18n={i18n} />} />
                {/* Operator-only data comes from the allow-listed ops API; this route is not in nav. */}
                <Route path="/ops/costs" element={<OpsCosts i18n={i18n} />} />
                <Route path="/ops/health" element={<OpsHealth i18n={i18n} />} />
                {/* The consent page an assistant's connection opens (the service sends it here). */}
                <Route path="/connect" element={<McpConnect i18n={i18n} />} />
                {/* The trusted review surface: the list a person opens, and the route a host's own
                `review_url` deep-links into (`/mcp/reviews/:review_id`). */}
                <Route path="/mcp/reviews" element={<McpReviews i18n={i18n} />} />
                <Route path="/mcp/reviews/:review_id" element={<McpReviews i18n={i18n} />} />
                {/* A device/human handoff's own screen; the operation id is the only thing a URL carries. */}
                <Route path="/mcp/handoffs/:operation_id" element={<McpHandoff i18n={i18n} />} />
                <Route path="*" element={<NotFound i18n={i18n} />} />
              </Routes>
            </Suspense>
          </RouteErrorBoundary>
        </main>

        <nav
          className="app-mobile-nav"
          data-testid="mobile-nav"
          aria-label={i18n.t('nav.main_label')}
        >
          {mobilePrimaryDestinations().map((destination) => (
            <span key={destination.id} className="app-mobile-nav__item">
              {navLink(destination)}
            </span>
          ))}
          {more.length > 0 ? (
            <button
              type="button"
              className={
                more.some((destination) => destination.id === section.id)
                  ? 'app-nav-link app-more-button is-active'
                  : 'app-nav-link app-more-button'
              }
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              data-testid="nav-more"
              onClick={() => setMoreOpen(true)}
            >
              <Icon name="menu" />
              <span className="app-nav-label">{i18n.t('nav.more')}</span>
            </button>
          ) : null}
        </nav>

        <Sheet
          open={moreOpen}
          title={i18n.t('nav.more')}
          closeLabel={i18n.t('common.close')}
          onClose={() => setMoreOpen(false)}
        >
          {signedInEmail === null ? null : (
            // Like the sidebar's foot: who is signed in, leading to the account (and Sign out).
            <Link
              to="/settings?section=account"
              className="app-more-account"
              data-testid="more-account"
              onClick={() => setMoreOpen(false)}
            >
              <Icon name="user" />
              <span>
                <span className="app-more-account__label">{i18n.t('nav.signed_in_as')}</span>
                <span className="app-more-account__email">{signedInEmail}</span>
              </span>
              <Icon name="chevron-right" />
            </Link>
          )}
          <ul className="app-more-list">
            {more.map((destination) => (
              <li key={destination.id}>
                {navLink(destination, 'app-nav-link', () => setMoreOpen(false))}
              </li>
            ))}
          </ul>
          {vault === null ? null : (
            <nav aria-labelledby="more-settings-label" data-testid="more-shortcuts">
              <h3 className="app-more-heading" id="more-settings-label">
                {i18n.t('nav.more_settings_label')}
              </h3>
              <ul className="app-more-list">
                {MORE_SHORTCUTS.map((shortcut) => (
                  <li key={shortcut.id}>
                    <Link
                      to={shortcut.path}
                      className="app-nav-link"
                      data-testid={`more-shortcut-${shortcut.id}`}
                      onClick={() => setMoreOpen(false)}
                    >
                      <Icon name={shortcut.icon} />
                      <span className="app-nav-label">{i18n.t(shortcut.label_key)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </Sheet>
      </div>
    </WorkspaceFrameContext.Provider>
  )
}

/**
 * The recipient journey is account-free: it renders outside the sender shell so a signer
 * never sees another person's navigation, vault or workspace (§7.2, REC-10).
 */
export function App({ i18n = createI18n({ locale: 'en' }) }: AppProps) {
  const location = useLocation()
  // A recipient, a CC reader or the installed apps' framed challenge has no Home in this app.
  const recipient = isOutsideShell(location.pathname)
  return (
    <RouteErrorBoundary i18n={i18n} resetKey={location.pathname} recipient={recipient}>
      <Suspense fallback={<RouteSkeleton i18n={i18n} />}>
        <Routes>
          <Route path="/sign/:token" element={<RecipientSign i18n={i18n} />} />
          <Route path="/cc/:token/final" element={<CcFinal i18n={i18n} />} />
          {/* The installed apps' hosted Turnstile challenge (option C): no shell, no account. */}
          <Route path="/native-challenge" element={<NativeChallenge />} />
          <Route path="*" element={<Shell i18n={i18n} />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  )
}

/** Placeholder for the S0.9 version banner wiring (service worker handshake). */
export function VersionBanner({
  i18n,
  onReload,
}: {
  readonly i18n: I18n
  readonly onReload: () => void
}) {
  return (
    <div role="status" className="app-version-banner">
      <span>{i18n.t('common.new_version_available')}</span>
      <Button variant="quiet" onClick={onReload}>
        {i18n.t('common.reload')}
      </Button>
    </div>
  )
}

/** The Paper bento wordmark: its first part in ink, its second in signature blue. */
function Wordmark({ className }: { readonly className?: string }): ReactElement {
  const [ink, accent] = BRAND.wordmark
  return (
    <span className={['app-wordmark', className].filter(Boolean).join(' ')}>
      {ink}
      <span className="app-wordmark__accent">{accent}</span>
    </span>
  )
}
